import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Mail, RefreshCw, Send, Copy, Download, Loader2 } from 'lucide-react';
import ExportDataPanel from './ExportDataPanel';

const EmailsPanel = ({ data, loading, onRefresh, filter, setFilter, search, setSearch, filtered, onCopy, onDownload, onBulkEmail, pin }) => {
  if (loading && !data) {
    return (
      <div className="space-y-4">
        <ExportDataPanel pin={pin} />
        <div className="text-center py-16"><Loader2 size={32} className="animate-spin text-red-500 mx-auto" /></div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-4">
        <ExportDataPanel pin={pin} />
        <div className="text-center py-12 rounded-lg" style={{ backgroundColor: 'var(--tp-dark)' }}>
          <Mail size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm mb-4">Clicca aggiorna per caricare l'elenco email</p>
          <Button onClick={onRefresh} data-testid="emails-load-btn" className="bg-red-600 hover:bg-red-700 text-white">
            <RefreshCw size={14} className="mr-2" /> CARICA EMAIL
          </Button>
        </div>
      </div>
    );
  }

  const sources = [
    { key: 'all', label: 'Tutti' },
    { key: 'iscrizioni', label: `Iscrizioni (${data.by_source.iscrizioni})` },
    { key: 'contatti', label: `Contatti (${data.by_source.contatti})` },
    { key: 'cypha_kings', label: `Cypha Kings (${data.by_source.cypha_kings})` },
    { key: 'fuori_cash', label: `Fuori Cash (${data.by_source.fuori_cash})` },
    { key: 'freestylers', label: `Freestylers (${data.by_source.freestylers})` },
  ];

  return (
    <div className="space-y-4">
      <ExportDataPanel pin={pin} />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div>
          <p className="text-white text-lg font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {filtered.length} email {filter !== 'all' ? `(${filter})` : 'uniche'}
          </p>
          <p className="text-gray-500 text-xs mt-0.5">Totale generale: {data.total}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={onBulkEmail} size="sm" data-testid="emails-bulk-btn" className="bg-red-600 hover:bg-red-700 text-white font-bold">
            <Send size={14} className="mr-1.5" /> Invia email di massa
          </Button>
          <Button onClick={onCopy} size="sm" data-testid="emails-copy-btn" className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-700">
            <Copy size={14} className="mr-1.5" /> Copia tutte
          </Button>
          <Button onClick={onDownload} size="sm" data-testid="emails-csv-btn" className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-700">
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button onClick={onRefresh} size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {sources.map(s => (
          <button
            key={s.key}
            data-testid={`emails-filter-${s.key}`}
            onClick={() => setFilter(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
              filter === s.key ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
            style={{ fontFamily: 'Oswald, sans-serif' }}>
            {s.label}
          </button>
        ))}
      </div>

      <Input
        data-testid="emails-search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Cerca email o nome..."
        className="bg-gray-900 border-gray-700 text-white"
      />

      <div className="rounded-lg border border-gray-800 overflow-hidden" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 sticky top-0 z-10">
              <tr className="text-left text-gray-400 text-xs uppercase tracking-wider">
                <th className="p-3">#</th>
                <th className="p-3">Email</th>
                <th className="p-3 hidden sm:table-cell">Nome</th>
                <th className="p-3 hidden md:table-cell">Sorgenti</th>
                <th className="p-3 hidden lg:table-cell">Data</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="5" className="p-6 text-center text-gray-500">Nessuna email trovata</td></tr>
              ) : filtered.map((e, i) => (
                <tr key={e.email} className="border-t border-gray-800 hover:bg-gray-900/50" data-testid={`email-row-${i}`}>
                  <td className="p-3 text-gray-600 text-xs">{i + 1}</td>
                  <td className="p-3 text-white font-mono text-xs break-all">
                    <a href={`mailto:${e.email}`} className="hover:text-red-400">{e.email}</a>
                  </td>
                  <td className="p-3 text-gray-300 hidden sm:table-cell">{e.nome || '—'}</td>
                  <td className="p-3 hidden md:table-cell">
                    <div className="flex gap-1 flex-wrap">
                      {e.sources.map(s => (
                        <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-gray-500 text-xs hidden lg:table-cell">
                    {e.created_at ? new Date(e.created_at).toLocaleDateString('it-IT') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmailsPanel;
